/**
 * Component tools
 */
import { EventEmitter } from '@billjs/event-emitter';
import utility from './functions';
import BaseComponent from '../components/component';

export const getComponent = (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  /**
   * Get component instance
   * 
   * @param {jQuery|HTMLElement} element
   * @param {String} [key] 
   * @return {BaseComponent|null} component instance
   */
  return function getComponent(element, key) {
    const el = $(element);

    if (!key) {
      key = el.attr('data-component');
    }

    if (!key) {
      return null;
    }
    
    if (el.is(`[data-component="${key}"]`)) {
      const inst = el.data(key);
      if (inst instanceof BaseComponent) {
        return inst;
      }
    }

    return null;
  };
})(jQuery, window);


export const queryComponents = (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  /**
   * Query components under specific context (exclusive).
   * 
   * @param {String} [key]
   * @param {jQuery|HTMLElement|string} context 
   */
  return function queryComponents(key, element) {
    let el = $(element || document.body);
    let elems;

    if (typeof key === 'string' && key) {
      elems = el.find(`[data-component="${key}"]`);
    }
    else {
      elems = el.find(`[data-component]`);
    }

    elems = elems
      .map((idx, elem) => {
        const name = $(elem).attr('data-component');
        if (!name) {
          return null;
        }

        const inst = getComponent(elem, name);
        if (!inst) {
          return null;
        }

        return {
          elem,
          inst,
          name
        };
      })
      .get()
      .filter(v => v !== null);

    return elems;
  };
})(jQuery, window);

export const waitInnerComponentsInitialized = (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  function stillLoading(obj) {
    obj.comps = obj.comps.filter((idx, elem) => {
      const inst = getComponent(elem);
      if (inst) {
        if (inst.element && inst.initializedClass) {
          return !inst.element.hasClass(inst.initializedClass);
        }

        return false;
      }
      
      return true;
    });

    return obj.comps.length;
  }

  /**
   * Wait all compoments under a specific element initialized.
   * 
   * @param {jQuery|HTMLElement} element
   * @param {String[]} names 
   */
  return function waitInnerComponentsInitialized(element, names = []) {
    return new Promise((resolve, reject) => {
      const el = $(element);
      let allComps = $(`[data-component]`, el);

      if (names) {
        if (typeof names === 'string') {
          names = [names];
        }
        if (Array.isArray(names) && names.length) {
          allComps = allComps.filter((idx, elem) => {
            return names.includes($(elem).attr('data-component'));
          });
        }
      }

      const obj = {
        comps: allComps
      };
      
      const checkLoading = () => {
        if (stillLoading(obj)) {
          window.setTimeout(() => {
            checkLoading();
          }, 1);
        }
        else {
          resolve(true);
        }
      };

      try {
        checkLoading();
      }
      catch (err) {
        reject(err);
      }
    });
    
  };
})(jQuery, window);


export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  const SELECTORS = 'selectors';
  const baseSelectors = BaseComponent[SELECTORS] || {};

  let loadedComponents = {};
  let loaders = {};

  /**
   * Extends a Component Class selectors, make it inherits from Base Class
   * 
   * @param {Class} componentClass 
   */
  const extendSelectors = (componentClass) => {
    const selectors = componentClass[SELECTORS] || {};
    componentClass[SELECTORS] = $.extend(true, {}, baseSelectors, selectors);
  };

  /**
   * Make Component Class selectors readonly.
   * 
   * @param {Class} componentClass 
   */
  const protectSelectors = (componentClass) => {
    if (componentClass[SELECTORS]) {
      const descriptor = Object.getOwnPropertyDescriptor(componentClass, SELECTORS);
      if (!descriptor.configurable || !descriptor.writable) {
        return;
      }
    }

    const selectors = componentClass.selectors || {};
    delete componentClass.selectors;

    Object.defineProperty(componentClass, SELECTORS, {
      value: selectors,
      configurable: false,
      writable: false,
      enumerable: true
    });
  };


  /**
   * Load a component
   *
   * @param {String} name Component Name
   * @returns {Promise}
   */
  const loadComponent = async (name) => {
    if (typeof loadedComponents[name] !== 'undefined') {
      return loadedComponents[name];
    }

    let needLoad = false;

    if (typeof loaders[name] === 'undefined') {
      loaders[name] = new EventEmitter();
      needLoad = true;
    }

    const ret = new Promise((resolve, reject) => {
      loaders[name].on('loaded', (ev) => {
        resolve(ev.data);
      });
      loaders[name].on('error', (ev) => {
        reject(ev.data);
      });
    });

    if (needLoad) {
      try {
        import(`../components/${name}.js`)
          .then(({ default: componentClass }) => {

            if (typeof componentClass === 'function') {
              extendSelectors(componentClass);
              protectSelectors(componentClass);
              loadedComponents[name] = componentClass;
            } else {
              // the imported value is not a function, means it is not a class
              loadedComponents[name] = false;
            }

            window.setTimeout(() => {
              loaders[name].fire('loaded', componentClass);
            });
          })
          .catch(err => {
            window.setTimeout(() => {
              loaders[name].fire('error', err);
            });
          });
      }
      catch (err) {
        // load error, stop trying to load it
        loadedComponents[name] = false;

        window.setTimeout(() => {
          loaders[name].fire('error', err);
        });
      }
    }

    return ret;
  };

  /**
   * Init specific component against given component elements (jQuery elements).
   * 
   * @param {String} name 
   * @param {HTMLElement[]} components 
   */
  const initComponents = async (name, components) => {
    loadComponent(name)
      .then(componentClass => {
        if (componentClass) {
          components.filter(`[data-component="${name}"]`).each(async (idx, elem) => {
            let component = getComponent(elem, name);

            if (!component) {
              // try to create instance
              try {
                component = new componentClass(elem);

              } catch (err) {
                component = null;

                // ignore
                // eslint-disable-next-line no-console
                console.log(err);
              }

              // execute component init script
              if (component) {
                try {
                  component.init();
                  $(elem).data(name, component);
                }
                catch (err) {
                  // eslint-disable-next-line no-console
                  console.log(err);
                }
              }
            }
          });
        }
      })
      .catch(err => {
        // ignore
        // eslint-disable-next-line no-console
        console.warn('error loading component', name, err);
      });
  };

  return {
    /**
     * Invoke all components initialization for specific context
     * 
     * @param {HTMLElement} [context] initialization context
     */
    invoke: context => {
      context = context || window.document || null;

      // query all elements with `data-component` attribute
      const components = $(`[data-component]`, context);

      // remove duplicates
      const componentNames = utility.uniqueArray(components.map((idx, elem) => {
        var s = ($(elem).data('component') || '').trim();
        return s ? s : null;
      }).get());

      // for each component:
      // - try to import the component JS, if it doesn't exist or failed, ignore.
      // - find all elements of this component then create instance for each of them.
      componentNames.forEach(name => {
        initComponents(name, components);
      });
    },

    getComponent: getComponent,
    queryComponents: queryComponents,
    waitInnerComponentsInitialized: waitInnerComponentsInitialized
  };
})(jQuery, window);